import React, { useEffect, useState } from "react";
import moment from "moment";
import { SelectBox } from "devextreme-react/select-box";
import { MasterModal } from "../../Shared/master";
import { DateFormat } from "../../../helpers/fixcodes";
import { axiosGet, axiosPost } from "../../../apis/useAxios";
import { askConfirmation, showErrorToast, showResponseMessage } from "../../../helpers/message";
import { isHotelDropOff } from "./crewActivityRules";

const initials = (row) => `${(row.firstName || row.fullName || "?")[0]}${(row.lastName || "")[0] || ""}`.toUpperCase();

/**
 * Change the crew's hotel for one activity (same API as before: CreweasyMobile/get-allocate-hotel-list, Hotel/get-hotel-transfer,
 * CreweasyMobile/change-hotel). Shows the crew, the activity and the current hotel; the new hotel shows its address and
 * who transports the crew there. Asks before changing.
 */
export default function ChangeHotelModal({ row, onChanged, onClose }) {
  const [hotels, setHotels] = useState(null); // null = loading
  const [choice, setChoice] = useState(null); // { id, code, name, address }
  const [transfer, setTransfer] = useState({ loading: false, name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const dropOff = isHotelDropOff(row);
  const current = { name: dropOff ? row.dropOffHotelName : row.hotelName, address: dropOff ? row.dropOffHotelAddress : row.hotelAddress };

  useEffect(() => {
    let active = true;
    axiosPost("CreweasyMobile/get-allocate-hotel-list", {
      AirlineCode: row.airlineCode,
      TransactionId: row.transactionId,
      EmployeeNumber: row.employeeNumber,
      LocationCode: row.activityLocationCode,
    })
      .then((r) => active && setHotels((r?.payload || []).filter((h) => h.id !== row.hotelId && h.id !== row.dropOffHotelId)))
      .catch((err) => {
        if (!active) return;
        setHotels([]);
        showErrorToast(err?.message || "Unable to load the hotels.");
      });
    return () => {
      active = false;
    };
  }, [row]);

  const pick = async (id) => {
    const hotel = (hotels || []).find((h) => h.id === id) || null;
    setChoice(hotel);
    setError("");
    setTransfer({ loading: Boolean(hotel?.code), name: "" });
    if (!hotel?.code) return;
    try {
      const r = await axiosGet("Hotel/get-hotel-transfer", { TransactionId: row.transactionId, EmployeeNumber: row.employeeNumber, hotelCode: hotel.code });
      setTransfer({ loading: false, name: r?.payload?.transporterName || "" });
    } catch {
      setTransfer({ loading: false, name: "" });
    }
  };

  const submit = async () => {
    if (!choice) {
      setError("Please select a hotel.");
      return;
    }
    if (!(await askConfirmation(`Are you sure you want to change <br/>the hotel to <strong>${choice.name}</strong>?`, "Yes", "No"))) return;
    setBusy(true);
    try {
      const response = await axiosPost("CreweasyMobile/change-hotel", {
        AirlineCode: row.airlineCode,
        TransactionId: row.transactionId,
        EmployeeNumber: row.employeeNumber,
        HotelId: choice.id,
        DeviceTime: moment().format(DateFormat.SqlDateTimeFullFormat),
      });
      const ok = await showResponseMessage({ isSuccess: response?.success, message: response?.message });
      if (ok && response?.success) {
        onChanged();
        onClose();
      }
    } catch (err) {
      showErrorToast(err?.message || "Unable to change the hotel.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <MasterModal isOpen title="Change hotel" size="min(600px, 94vw)" busy={busy} saveText="Change hotel" onSubmit={submit} onClose={onClose}>
      <div className="cra-hotel">
        <div className="cra-hotel__crew">
          {row.crewProfilePhotoURL ? (
            <img src={row.crewProfilePhotoURL} alt="" className="cra-hotel__photo" />
          ) : (
            <span className="cra-hotel__photo">{initials(row)}</span>
          )}
          <div>
            <strong>{`${row.firstName || ""} ${row.lastName || ""}`.trim() || row.fullName}</strong>
            <span>
              {row.employeeNumber}
              {row.crewRank ? ` · ${row.crewRank}` : ""}
            </span>
          </div>
        </div>

        <dl className="cra-hotel__facts">
          {row.activityDisplay && (
            <div>
              <dt>Activity</dt>
              <dd>{row.activityDisplay}</dd>
            </div>
          )}
          {row.activityTimeDisplay && (
            <div>
              <dt>Time</dt>
              <dd>{moment(row.activityTimeDisplay).format(DateFormat.MomentDisplayDateTime24Format)}</dd>
            </div>
          )}
          {row.airportCode && (
            <div>
              <dt>Airport</dt>
              <dd>{row.airportCode}</dd>
            </div>
          )}
          {(row.transportationCompany || row.transportationTypeName) && (
            <div>
              <dt>Transport</dt>
              <dd>{row.transportationCompany || row.transportationTypeName}</dd>
            </div>
          )}
        </dl>

        <div className="cra-hotel__box">
          <span className="cra-hotel__label">Current hotel{dropOff ? " (drop-off)" : ""}</span>
          <strong>{current.name || "—"}</strong>
          {current.address && <small>{current.address}</small>}
        </div>

        <div className="form-group mst-field">
          <label htmlFor="cra-new-hotel">
            New hotel<span className="text-danger">*</span>
          </label>
          <SelectBox
            dataSource={hotels || []}
            displayExpr="name"
            valueExpr="id"
            value={choice?.id ?? null}
            searchEnabled
            placeholder={hotels === null ? "Loading hotels..." : "Select a hotel"}
            disabled={hotels === null || transfer.loading}
            inputAttr={{ id: "cra-new-hotel", "aria-label": "New hotel" }}
            onValueChanged={(e) => pick(e.value)}
          />
          {hotels && !hotels.length && <span className="mst-drawer-hint">No other hotel can be allocated for this activity.</span>}
          {error && <span className="invalid-feedback d-block">{error}</span>}
        </div>

        {choice && (
          <div className="cra-hotel__box is-new">
            <span className="cra-hotel__label">New hotel</span>
            <strong>{choice.name}</strong>
            {choice.address && <small>{choice.address}</small>}
            <span className="cra-hotel__transfer">
              <i className="fas fa-shuttle-van" aria-hidden="true" />{" "}
              {transfer.loading ? "Checking who transports the crew..." : transfer.name || "No transport found for this hotel"}
            </span>
          </div>
        )}
      </div>
    </MasterModal>
  );
}
